/**
 * Tenant-facing `/v1/tickets` wire contract (#1394).
 *
 * FIELD NAMES ARE FROZEN BY THE SHIPPED DESKTOP CLIENT. The NaDouTongToken
 * desktop app parses exactly these camelCase keys (its Rust layer's contract
 * comments are the authoritative source: `src-tauri/src/lib.rs` around
 * `tickets_list`); the server matches the client, not the other way round.
 * Two response fields are additive extensions the client tolerates today and
 * will render once its UI catches up: `total` (page count) and the `status`
 * filter it enables.
 *
 * Request quirk inherited from the client: `email` arrives as JSON `null`
 * (never an omitted key) when the user left it blank — serde serializes
 * `Option::None` as null. Handlers must treat null and absent identically.
 */

export type TicketType = 'billing' | 'usage' | 'api' | 'model' | 'other';
export type TicketStatus = 'open' | 'processing' | 'resolved' | 'closed';
/** `status` query filter: the four states plus `all` (the default). */
export type TicketStatusFilter = TicketStatus | 'all';
export type TicketReplyAuthor = 'user' | 'vendor';

export const TICKET_TYPES: readonly TicketType[] = [
  'billing',
  'usage',
  'api',
  'model',
  'other',
];

export const TICKET_STATUSES: readonly TicketStatus[] = [
  'open',
  'processing',
  'resolved',
  'closed',
];

/** Description and user-reply length bound (trimmed), tenant surface only —
 * vendor replies written by ops are deliberately unbounded (#1394). */
export const TICKET_TEXT_MAX_LENGTH = 500;

/** Attachment rules the client pre-validates and the server re-verifies. */
export const TICKET_MAX_ATTACHMENTS = 3;
export const TICKET_ATTACHMENT_MAX_BYTES = 2 * 1024 * 1024;
/** Exact-match whitelist — narrower than finance attachments' RFC-token
 * check on purpose: the desktop client only ever produces these four. */
export const TICKET_ATTACHMENT_CONTENT_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
];

/** Whole-request cap for POST /v1/tickets (10 MiB, #1394). */
export const TICKET_CREATE_MAX_BODY_BYTES = 10 * 1024 * 1024;

/** Anti-abuse: an org may hold at most this many open+processing tickets. */
export const TICKET_MAX_OPEN_PER_ORG = 20;

export interface TicketSummary {
  readonly id: string;
  readonly type: TicketType;
  readonly description: string;
  readonly email: string;
  readonly status: TicketStatus;
  readonly attachmentCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListTicketsResponse {
  readonly tickets: readonly TicketSummary[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

export interface CreateTicketAttachmentPayload {
  readonly filename: string;
  readonly contentType: string;
  /** Raw base64 (no data: URL prefix — the client strips it). */
  readonly dataBase64: string;
}

export interface CreateTicketRequest {
  readonly type: TicketType;
  /** Nullable: null / absent / '' all mean "fall back to the session
   * account's email". */
  readonly email?: string | null;
  readonly description: string;
  /** Always present from the shipped client (empty array when none). */
  readonly attachments?: readonly CreateTicketAttachmentPayload[] | null;
}

export interface CreateTicketResponse {
  readonly ticket: TicketSummary;
}

export interface TicketReplyView {
  readonly id: string;
  readonly ticketId: string;
  readonly author: TicketReplyAuthor;
  readonly content: string;
  readonly createdAt: string;
}

/** Replies are returned in `createdAt` ASCENDING order — the client renders
 * them as received and never sorts. */
export interface ListTicketRepliesResponse {
  readonly replies: readonly TicketReplyView[];
}

export interface CreateTicketReplyRequest {
  readonly content: string;
}

export interface CreateTicketReplyResponse {
  readonly reply: TicketReplyView;
}

/**
 * Attachment metadata as returned by `GET /v1/tickets/:id/attachments` (#2070).
 *
 * Wire keys are camelCase and frozen by the same desktop-client rule as the
 * rest of this file. `filename` matches {@link CreateTicketAttachmentPayload}
 * (the DB column is `file_name`).
 *
 * The companion content endpoint `GET /v1/tickets/:id/attachments/:attachmentId`
 * returns the stored object BYTES — not JSON, not a base64 envelope.
 * `Content-Type` is the row's `content_type` only after it is in
 * {@link TICKET_ATTACHMENT_CONTENT_TYPES}; a stored value outside that closed
 * set is `500 INTERNAL` (same invariant-violation handling as a metadata row
 * whose object is missing) and is never copied into the header. The response
 * also sets `X-Content-Type-Options: nosniff` and
 * `Cache-Control: private, no-store`. `private` keeps the bytes out of
 * shared/CDN caches; `no-store` is required because the cache key is the URL
 * (no cookie, no revalidation) — a `max-age` hit would skip the visibility
 * check and let a later session on the same browser profile open a support
 * screenshot from disk. A 404 on either read endpoint uses the same visibility
 * rule as the rest of `/v1/tickets`: a ticket the caller cannot see (missing,
 * other-org, other-member) is `TICKET_NOT_FOUND`, never 403. An `attachmentId`
 * that is not on that visible ticket is `ATTACHMENT_NOT_FOUND` (404), matching
 * the ops internal download surface — it does not confirm whether the id
 * exists on another ticket.
 */
export interface TicketAttachmentView {
  readonly id: string;
  readonly filename: string;
  readonly contentType: string;
  readonly sizeBytes: number;
}

/**
 * Attachment list order is stable across calls (`ORDER BY created_at ASC, id ASC`,
 * matching ops) but is **not** upload order. Same-ticket attachments are
 * inserted in one `DB.transaction`, so PostgreSQL `CURRENT_TIMESTAMP` stamps
 * them with the transaction-start time and every row shares it; `id` is
 * `tka_` + 8 random hex bytes and is not monotonic. The id tie-break is
 * therefore a repeatable permutation, not the client's array order.
 */
export interface ListTicketAttachmentsResponse {
  readonly attachments: readonly TicketAttachmentView[];
}

export function isTicketType(value: unknown): value is TicketType {
  return typeof value === 'string' && (TICKET_TYPES as readonly string[]).includes(value);
}

export function isTicketStatus(value: unknown): value is TicketStatus {
  return typeof value === 'string' && (TICKET_STATUSES as readonly string[]).includes(value);
}
